/**
 * Breadcrumbs Component
 * Page navigation breadcrumbs
 */

import React from 'react';
import { Breadcrumbs as MuiBreadcrumbs, Link, Typography, Box } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { tokens } from '../../theme/tokens';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

const routeMapping: Record<string, string> = {
  dashboard: 'Dashboard',
  upload: 'Upload Excel',
  allocation: 'Allocation',
  matrix: 'Allocation Matrix',
  adjust: 'Manual Adjustment',
  review: 'Review & Approve',
  history: 'History',
  employee: 'Employee',
  'my-work': 'My Work',
  reports: 'Reports',
  admin: 'Admin',
  config: 'Configuration',
  integrations: 'Integrations',
};

const Breadcrumbs: React.FC = () => {
  const location = useLocation();

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter((path) => path);
    
    // Always start with home
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', path: '/dashboard' },
    ];

    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      const label = routeMapping[path] || path.charAt(0).toUpperCase() + path.slice(1);
      
      // Last item doesn't need a path (current page)
      if (index === paths.length - 1) {
        breadcrumbs.push({ label });
      } else {
        breadcrumbs.push({ label, path: currentPath });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on home/dashboard
  if (location.pathname === '/' || location.pathname === '/dashboard') {
    return null;
  }

  return (
    <Box sx={{ 
      mb: 3,
    }}>
      <MuiBreadcrumbs
        component="nav"
        aria-label="Breadcrumb navigation"
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#D1D5DB' }} aria-hidden="true" />}
      >
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          if (isLast) {
            return (
              <Typography
                key={crumb.label}
                color="text.primary"
                aria-current="page"
                sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}
              >
                {index === 0 && <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />}
                {crumb.label}
              </Typography>
            );
          }

          return (
            <Link
              key={crumb.label}
              component={RouterLink}
              to={crumb.path || '/'}
              underline="hover"
              color="inherit"
              sx={{
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  color: tokens.colors.primary.main,
                },
              }}
            >
              {index === 0 && <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />}
              {crumb.label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
